import { Injectable } from '@angular/core';
import { Firestore, collection, collectionData, doc, updateDoc, getDoc, addDoc, deleteDoc } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, from, shareReplay } from 'rxjs';

export interface PriceOption {
  name: string;
  name_de: string;
  name_en: string;
  name_es: string;
  name_fr: string;
  name_nl: string;
  order: number;
  price: string | number;
  var_id: number;
  stock_quantity?: number;
  stock_status?: 'instock' | 'outofstock';
  enabled?: boolean;
  isEditing?: boolean;
  editPrice?: number;
}

export interface Product {
  id?: string;
  sku: string;
  acf: {
    naam_nl: string;
    [key: string]: any; // Allow additional ACF fields
  };
  price: number;
  price_options?: PriceOption[];
  stock_quantity: number;
  stock_status: 'instock' | 'outofstock' | 'onbackorder';
  // Properties for price editing
  isEditingPrice?: boolean;
  editPrice?: number;
  // Property to toggle price options view
  showPriceOptions?: boolean;
  // Allow additional WooCommerce fields (using any to allow flexibility)
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private productsCache$: Observable<Product[]> | null = null;

  constructor(private firestore: Firestore, private functions: Functions) {}

  getProducts(): Observable<Product[]> {
    // Cache the observable using shareReplay to avoid multiple subscriptions
    // This ensures the Firestore listener is only established once
    if (!this.productsCache$) {
      const productsRef = collection(this.firestore, 'dwt_products');
      const productsData$ = collectionData(productsRef, { idField: 'id' }) as Observable<Product[]>;
      this.productsCache$ = productsData$.pipe(
        shareReplay(1)
      );
    }
    return this.productsCache$;
  }

  // Method to invalidate cache when needed (e.g., after updates)
  invalidateCache(): void {
    this.productsCache$ = null;
  }

  async toggleStockStatus(productId: string, currentStatus: string) {
    const newStatus = currentStatus === 'instock' ? 'outofstock' : 'instock';
    const productRef = doc(this.firestore, 'dwt_products', productId);
    await updateDoc(productRef, { stock_status: newStatus });
  }

  updateProductPrice(productId: string, newPrice: number): Observable<void> {
    const productRef = doc(this.firestore, 'dwt_products', productId);
    return from(updateDoc(productRef, { price: newPrice }));
  }

  updateProductPriceOptions(productId: string, priceOptions: PriceOption[]): Observable<void> {
    const productRef = doc(this.firestore, 'dwt_products', productId);
    return from(updateDoc(productRef, { price_options: priceOptions }));
  }

  async getProductById(productId: string): Promise<Product | null> {
    const productRef = doc(this.firestore, 'dwt_products', productId);
    const productSnap = await getDoc(productRef);
    
    if (productSnap.exists()) {
      return { id: productSnap.id, ...productSnap.data() } as Product;
    }
    return null;
  }

  async updateProduct(productId: string, productData: Partial<Product>): Promise<void> {
    const productRef = doc(this.firestore, 'dwt_products', productId);
    await updateDoc(productRef, productData);
  }

  async updateProductDomains(productId: string, domains: string[]): Promise<void> {
    const productRef = doc(this.firestore, 'dwt_products', productId);
    await updateDoc(productRef, { domains: domains });
  }

  async deleteProduct(productId: string): Promise<void> {
    const productRef = doc(this.firestore, 'dwt_products', productId);
    await deleteDoc(productRef);
    this.invalidateCache();
  }

  async addProduct(productData: Partial<Product>): Promise<string> {
    const productsRef = collection(this.firestore, 'dwt_products');
    const docRef = await addDoc(productsRef, productData);
    this.invalidateCache();
    return docRef.id;
  }

  async syncProductToWooCommerce(firestoreId: string, productData: Partial<Product>): Promise<{ wc_product_id: number }> {
    const callable = httpsCallable<
      { firestoreId: string; productData: Partial<Product> },
      { success: boolean; wc_product_id: number }
    >(this.functions, 'createWooCommerceProduct');

    const result = await callable({ firestoreId, productData });
    return { wc_product_id: result.data.wc_product_id };
  }

  async updateProductInWooCommerce(wcProductId: number, productData: Partial<Product>): Promise<void> {
    const callable = httpsCallable<
      { wcProductId: number; productData: Partial<Product> },
      { success: boolean }
    >(this.functions, 'updateWooCommerceProduct');

    await callable({ wcProductId, productData });
  }

  async compareWithWooCommerce(firestoreId: string): Promise<WcCompareResult> {
    const callable = httpsCallable<
      { firestoreId: string },
      WcCompareResult
    >(this.functions, 'compareWooCommerceProduct');

    const result = await callable({ firestoreId });
    return result.data;
  }

  async syncFirestoreToWoo(firestoreId: string): Promise<void> {
    const callable = httpsCallable<
      { firestoreId: string },
      { success: boolean }
    >(this.functions, 'syncFirestoreToWoo');

    await callable({ firestoreId });
  }

  async syncWooToFirestore(firestoreId: string): Promise<void> {
    const callable = httpsCallable<
      { firestoreId: string },
      { success: boolean }
    >(this.functions, 'syncWooToFirestore');

    await callable({ firestoreId });
  }

  async getWooCommerceCategories(): Promise<WcCategory[]> {
    const callable = httpsCallable<
      Record<string, never>,
      { categories: WcCategory[] }
    >(this.functions, 'getWooCommerceCategories');

    const result = await callable({});
    return result.data.categories;
  }
}

export interface WcFieldDifference {
  field: string;
  fsPath: string;
  wcPath: string;
  fsValue: string;
  wcValue: string;
}

export interface WcCompareResult {
  linked: boolean;
  wcProductId?: number;
  wcNotFound?: boolean;
  inSync?: boolean;
  differences: WcFieldDifference[];
  message: string;
}

export interface WcCategory {
  id: number;
  name: string;
  slug: string;
  parent: number;
  count: number;
} 